import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";
import { Helmet } from "react-helmet-async";
import { Grid, PageWrapper } from "components/Containers";
import { Banner, BannerShowcaseSignup } from "components/Banner";
import { useAuth, useMissionRoles } from "hooks";
import { css } from "@emotion/react";
import { APPLICANT_LIST_COLUMNS, STICKY_FILTER_KEYS_FOR_CLIENTS } from "constants/index";
import {
  ApplicantListLayout,
  ApplicantPdfDownloadLink,
  ApplicationDetailsModalForClient,
  ApplicationRateForClient,
} from "components/Missions/Applicants";
import PageTitle from "components/PageTitle";
import Card from "components/Card";
import NoResults from "components/Messages/NoResults";
import { Spinner } from "components/Loader";
import { ProfileLink } from "components/Profile";
import { MissionLink } from "components/Missions";
import { RoleLink } from "components/Missions/Roles";

/**
 * Applicants Page
 */
const Applicants = () => {
  const { state } = useLocation();
  const { missionRole } = state || {};
  const { isShowcase, isClient, canViewUnpublishedRoles } = useAuth();
  const intl = useIntl();
  const [defaults, setDefaults] = useState({});
  const { data, loading } = useMissionRoles({ filters: { published: !canViewUnpublishedRoles } });
  const { canReadUnpublishedRoles } = useAuth();

  /**
   * @description For showcase users, we need to set a default missionId, missionRoleIds, and organizationId because
   * the filters are hidden
   */
  useEffect(() => {
    if (data?.length === 0 || loading) return;

    const selectedRole = data.find(({ id }) => id === missionRole?.id) ?? data[0];
    setDefaults({
      missionId: selectedRole.mission.id,
      missionRoleIds: [{ name: selectedRole.name, id: selectedRole.id }],
      organizationId: selectedRole.mission.organization.id,
    });
  }, [data, loading, missionRole]);

  return (
    <PageWrapper>
      <Helmet>
        <title>{intl.formatMessage({ id: "Meta.Missions.Applicants.Title" })}</title>
      </Helmet>
      <BannerShowcaseSignup
        css={css`
          margin: 4rem 0 1rem 0;
        `}
      />
      {isShowcase && (
        <Banner keyName="showClientApplicantsBanner">
          <FormattedMessage
            id="Applicants.Banner.Title1"
            values={{
              intro: (
                <b>
                  <FormattedMessage id="Applicants.Banner.Title2" />
                </b>
              ),
            }}
          />
        </Banner>
      )}
      <Grid>
        <Grid.col start={1} end={13}>
          <PageTitle title={<FormattedMessage id={`Missions.Applicants.H1`} />} />
        </Grid.col>
        <Grid.col start={1} end={13}>
          {defaults?.missionId && defaults?.missionRoleIds?.length ? (
            <ApplicantListLayout
              defaultSelected={defaults}
              showOnlyPublishedMissions={isClient}
              showOnlyPublishedRoles={canReadUnpublishedRoles}
              showAdvancedOptions={false}
              initialProgressMenu={2}
              hideOrganizationFilter={true}
              hideMemberFilter={true}
              hideTabs={true}
              hideAdvancedFilters={false}
              hideSorts={true}
              enableCountQueries={true}
              variablesProp={{ includeScores: true }}
              columns={[
                APPLICANT_LIST_COLUMNS.member,
                APPLICANT_LIST_COLUMNS.mission,
                APPLICANT_LIST_COLUMNS.hourlyRateRange,
                APPLICANT_LIST_COLUMNS.profileAvailability,
                APPLICANT_LIST_COLUMNS.noticePeriod,
                APPLICANT_LIST_COLUMNS.applied,
                APPLICANT_LIST_COLUMNS.matchScore,
                APPLICANT_LIST_COLUMNS.viewButton,
              ]}
              initialStickyFilterKeys={STICKY_FILTER_KEYS_FOR_CLIENTS}
              applicationModalComponent={ApplicationDetailsModalForClient}
              modalConfigFunc={(application) => ({
                openModalOnClick: true,
                withOverlay: false,
                title: (
                  <FormattedMessage
                    id="Missions.MissionRole.ApplicationDetailsModal.Header"
                    values={{
                      org: <ProfileLink profile={application?.profile} />,
                      mission: <MissionLink data={application?.mission} withOverlay />,
                      role: (
                        <RoleLink mission={application?.mission} missionRole={application?.missionRole} withOverlay />
                      ),
                    }}
                  />
                ),
                info: (
                  <div css={styles.modal_info}>
                    {(isClient || isShowcase) && <ApplicantPdfDownloadLink applicant={application} />}
                    <ApplicationRateForClient application={application} />
                  </div>
                ),
                tabTitles: {
                  code_challenges: (
                    <FormattedMessage id="Missions.Applicants.ClientApplicationDetailsModal.TitleTechnicalAssessment" />
                  ),
                  profile: <FormattedMessage id="Missions.Applicants.ClientApplicationDetailsModal.TitleProfile" />
                },
                application,
              })}
            />
          ) : !loading ? (
            <Card>
              <NoResults
                title={"Missions.Applicants.NoResultsTitle"}
                description={"Missions.Applicants.NoResultsDescription"}
              />
            </Card>
          ) : (
            <Card>
              <Spinner />
            </Card>
          )}
        </Grid.col>
      </Grid>
    </PageWrapper>
  );
};

const styles = {
  modal_info: css`
    display: flex;
    align-items: center;
    gap: 12px;
  `,
};

export default Applicants;
